import { Contact, ContactGuid } from '@breezy/shared'
import { useMemo } from 'react'
import { useExpectedCompany } from '../../providers/PrincipalUser'
import { useOnQueryData } from '../react-query'
import { trpc } from '../trpc'

type OnDataMode = 'once' | 'every-update'

export const useFetchContactByContactGuidQuery = ({
  contactGuid,
  opts = {},
}: {
  contactGuid: ContactGuid
  opts?: {
    enabled?: boolean
    /**
     * Fires when `query.data` is available, regardless of whether it was fetched
     * from the network or served from React Query cache.
     */
    onData?: (contact: Contact) => void
    /**
     * - 'once' (default): call `onData` at most once per (companyGuid, contactGuid) for this hook instance
     * - 'every-update': call `onData` whenever the underlying query data updates
     */
    onDataMode?: OnDataMode
  }
}) => {
  const companyGuid = useExpectedCompany().companyGuid

  const query = trpc.contacts['contacts:query'].useQuery(
    {
      type: 'by-contact-guid',
      companyGuid,
      contactGuid,
    },
    {
      select: data => {
        if (data.length !== 1) {
          throw new Error(`Failed to locate contact by guid = ${contactGuid}`)
        }
        return data[0]
      },
      enabled: opts.enabled,
    },
  )

  const notifyKey = useMemo(() => `${companyGuid}:${contactGuid}`, [companyGuid, contactGuid])
  const onData = opts.onData
  const onDataMode = opts.onDataMode ?? 'once'
  const enabled = opts.enabled !== false

  useOnQueryData({
    query,
    onData: onData ?? (() => {}),
    notifyKey,
    mode: onDataMode,
    enabled: enabled && !!onData,
  })

  return query
}
